import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Link2,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Sparkles,
  ArrowRight,
  RotateCcw,
  Film,
  Upload,
  Plus,
  ChevronRight,
  ShieldCheck,
  ArrowLeft,
  X,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useImportDeliverableUrls } from '../../hooks/useImportDeliverableUrls';
import { normalizeInstagramPostUrl, extractInstagramShortcode } from '../../validators/deliverable.validators';
import { getLimitExceededMeter, toFriendlyLimitError } from '@/lib/limitErrors';
import { cn } from '@/lib/utils';

export interface UploadUrlsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  onSelectAddPost?: () => void;
}

const MAX_URLS_PER_IMPORT = 50;

export const UploadUrlsModal: React.FC<UploadUrlsModalProps> = ({
  open,
  onOpenChange,
  campaignId,
  onSelectAddPost,
}) => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id ?? '';

  const [mode, setMode] = useState<'choose' | 'url' | 'csv'>('choose');
  const [textInput, setTextInput] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvExtractedUrls, setCsvExtractedUrls] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    isProcessing,
    currentIndex,
    totalCount,
    results,
    isComplete,
    processUrls,
    retryFailed,
    reset,
    cancel,
  } = useImportDeliverableUrls(campaignId, orgId);

  useEffect(() => {
    if (open) {
      setMode('choose');
      setTextInput('');
      setCsvFile(null);
      setCsvExtractedUrls([]);
      reset();
    }
  }, [open, reset]);

  // Extract, validate, normalize, and deduplicate URLs by Instagram shortcode in memory for URL mode
  const { normalizedUrls, rawCount, duplicateCount, isCapped } = useMemo(() => {
    const lines = textInput
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const validUrls: string[] = [];
    const seenKeys = new Set<string>();
    let duplicates = 0;

    for (const line of lines) {
      const normalized = normalizeInstagramPostUrl(line);
      if (normalized) {
        const shortcode = extractInstagramShortcode(normalized);
        const dedupeKey = shortcode ? `ig:${shortcode}` : normalized;
        if (seenKeys.has(dedupeKey)) {
          duplicates++;
        } else {
          seenKeys.add(dedupeKey);
          validUrls.push(normalized);
        }
      }
    }

    const isCapped = validUrls.length > MAX_URLS_PER_IMPORT;
    const finalUrls = isCapped ? validUrls.slice(0, MAX_URLS_PER_IMPORT) : validUrls;

    return {
      normalizedUrls: finalUrls,
      rawCount: validUrls.length + duplicates,
      duplicateCount: duplicates,
      isCapped,
    };
  }, [textInput]);

  const handleOpenChange = (newOpen: boolean) => {
    if (isProcessing) return; // Prevent closing mid-process
    if (!newOpen) {
      setMode('choose');
      setTextInput('');
      setCsvFile(null);
      setCsvExtractedUrls([]);
      reset();
    }
    onOpenChange(newOpen);
  };

  // Parse CSV or text file containing links
  const handleFileRead = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (!content) return;

      const lines = content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      const extracted: string[] = [];
      const seenKeys = new Set<string>();

      for (const line of lines) {
        // Split by commas/tabs while preserving URL integrity
        const tokens = line.split(/[,\t]+/).map((s) => s.trim().replace(/^["']|["']$/g, ''));
        for (const token of tokens) {
          const norm = normalizeInstagramPostUrl(token);
          if (norm) {
            const shortcode = extractInstagramShortcode(norm);
            const dedupeKey = shortcode ? `ig:${shortcode}` : norm;
            if (!seenKeys.has(dedupeKey)) {
              seenKeys.add(dedupeKey);
              extracted.push(norm);
            }
          }
        }
      }

      if (extracted.length === 0) {
        toast({
          title: 'No Instagram links found',
          description: 'The selected file does not contain any valid Instagram Reel or Post links.',
          variant: 'destructive',
        });
        setCsvFile(file);
        setCsvExtractedUrls([]);
        return;
      }

      const capped = extracted.slice(0, MAX_URLS_PER_IMPORT);
      setCsvFile(file);
      setCsvExtractedUrls(capped);

      toast({
        title: 'Links loaded',
        description: `Imported ${capped.length} unique link${capped.length === 1 ? '' : 's'} from ${file.name}.`,
      });
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileRead(file);
    }
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileRead(file);
    }
  };

  const handleStartUrlImport = async () => {
    if (normalizedUrls.length === 0) {
      toast({
        title: 'No Instagram links found',
        description: 'Please paste at least one valid Instagram Reel or Post link.',
        variant: 'destructive',
      });
      return;
    }

    await processUrls(normalizedUrls);
  };

  const handleStartCsvImport = async () => {
    if (csvExtractedUrls.length === 0) {
      toast({
        title: 'No Instagram links found',
        description: 'Please upload a CSV or TXT file containing valid Instagram Reel or Post links.',
        variant: 'destructive',
      });
      return;
    }

    await processUrls(csvExtractedUrls);
  };

  const handleRetryFailed = async () => {
    await retryFailed();
  };

  // Summary counts
  const successfulCount = results.filter((r) => r.status === 'success').length;
  const failedCount = results.filter((r) => r.status === 'failed').length;
  const newCreatorsCount = results.filter(
    (r) => r.status === 'success' && r.influencer?.is_new_in_crm
  ).length;

  // Check if any error was caused by a subscription quota limit
  const limitFailure = results.find(
    (r) =>
      r.status === 'failed' &&
      (r.code?.includes('limit_exceeded') ||
        (r.error && getLimitExceededMeter(r.error) !== null) ||
        r.error?.includes('limit_exceeded') ||
        r.error?.includes("reached your plan's") ||
        r.error?.includes("reached this month's"))
  );

  const friendlyLimitNotice = limitFailure
    ? toFriendlyLimitError(
      limitFailure.error,
      "You've reached your organization's subscription limit. Upgrade your plan in Settings to import more deliverables."
    )
    : null;

  const progressPercentage = totalCount > 0 ? Math.round((currentIndex / totalCount) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "font-['Hanken_Grotesk',sans-serif] bg-white border border-slate-100 shadow-2xl overflow-hidden",
          mode === 'choose' && !isProcessing && !isComplete
            ? 'max-w-[540px] p-6 sm:p-7 rounded-[24px]'
            : isProcessing || isComplete
              ? 'sm:max-w-[580px] p-6 rounded-[24px] max-h-[90vh] flex flex-col'
              : 'sm:max-w-[580px] p-6 rounded-[24px]'
        )}
      >
        {/* Processing or Results View */}
        {isProcessing || isComplete ? (
          <>
            <DialogHeader className="pb-3 border-b border-slate-100 flex flex-row items-center gap-3 text-left">
              <div className="w-10 h-10 rounded-full bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#E9D5FF]">
                <Link2 className="w-5 h-5 stroke-[2]" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  {isProcessing ? 'Importing Deliverables...' : 'Import Complete'}
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Automatically identifying creators and importing deliverables.
                </p>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {/* Progress Bar Header */}
              <div className="space-y-2 bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-700">
                  <span className="flex items-center gap-1.5">
                    {isProcessing && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#A655F8]" />}
                    {isProcessing ? `Processing ${currentIndex} of ${totalCount}...` : 'Processing Complete'}
                  </span>
                  <span>{progressPercentage}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-[#A655F8] h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                {isComplete && (
                  <div className="text-[12px] text-slate-600 pt-1 flex items-center justify-between">
                    <span>
                      {successfulCount} succeeded · {failedCount} failed
                    </span>
                    {newCreatorsCount > 0 && (
                      <span className="text-[#A655F8] font-medium">
                        ✨ {newCreatorsCount} new creator{newCreatorsCount === 1 ? '' : 's'} added to CRM
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Plan Limit Exceeded Prompt Banner */}
              {friendlyLimitNotice && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start justify-between gap-3 shadow-xs">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold block">Plan limit reached</span>
                      <span className="text-amber-800 text-[11px] leading-relaxed block mt-0.5">
                        {friendlyLimitNotice}
                      </span>
                    </div>
                  </div>
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="bg-white hover:bg-amber-100 border-amber-300 text-amber-900 text-xs h-7 px-3 rounded-lg shrink-0 font-medium"
                  >
                    <Link to="/settings">Upgrade Plan</Link>
                  </Button>
                </div>
              )}

              {/* Live Items Feed */}
              <div className="max-h-[280px] overflow-y-auto space-y-2 pr-1">
                {results.map((item, idx) => {
                  const isLimitError =
                    item.status === 'failed' &&
                    (item.code?.includes('limit_exceeded') ||
                      (item.error && getLimitExceededMeter(item.error) !== null) ||
                      item.error?.includes('limit_exceeded') ||
                      item.error?.includes("reached your plan's") ||
                      item.error?.includes("reached this month's"));

                  return (
                    <div
                      key={`${item.url}-${idx}`}
                      className="p-2.5 rounded-xl border border-slate-100 bg-white shadow-xs flex items-center justify-between gap-3 text-xs"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {/* Reel Thumbnail if available */}
                        {item.submission?.thumbnail_url ? (
                          <div className="w-9 h-12 rounded-lg overflow-hidden border border-slate-200 bg-slate-900 shrink-0 shadow-xs relative">
                            <img
                              src={item.submission.thumbnail_url}
                              alt="Reel preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-9 h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 text-slate-400">
                            <Film className="w-4 h-4 text-slate-400" />
                          </div>
                        )}

                        {/* Creator Avatar & Info */}
                        <div className="flex items-center gap-2 min-w-0">
                          {item.influencer ? (
                            <Avatar className="w-7 h-7 shrink-0 border border-slate-100">
                              <AvatarImage src={item.influencer.avatar_url || undefined} />
                              <AvatarFallback className="bg-[#FAF5FF] text-[#A655F8] font-semibold text-[10px]">
                                {item.influencer.handle.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0 text-slate-400">
                              <Link2 className="w-3.5 h-3.5" />
                            </div>
                          )}

                          <div className="min-w-0">
                            {item.influencer ? (
                              <div className="font-semibold text-slate-800 truncate">
                                @{item.influencer.handle}
                                {item.influencer.name && item.influencer.name !== item.influencer.handle && (
                                  <span className="font-normal text-slate-400 ml-1.5">
                                    ({item.influencer.name})
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="font-mono text-slate-700 truncate max-w-[200px]">
                                {item.url}
                              </div>
                            )}

                            <div className="text-[11px] text-slate-400 truncate max-w-[240px]">
                              {item.url}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status Badges */}
                      <div className="shrink-0 text-right">
                        {item.status === 'success' ? (
                          item.submission?.is_existing ? (
                            <span
                              title="Deliverable already in campaign — metrics refreshed"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200"
                            >
                              <CheckCircle2 className="w-3 h-3 text-sky-600" />
                              Already Exists (Refreshed)
                            </span>
                          ) : item.influencer?.is_new_in_crm ? (
                            <span
                              title="Added Influencer to CRM & Campaign + Reel Linked"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-purple-50 text-[#7E22CE] border border-purple-200"
                            >
                              <CheckCircle2 className="w-3 h-3 text-[#A655F8]" />
                              Added Influencer to CRM & Campaign + Reel Linked
                            </span>
                          ) : item.influencer?.is_new_in_campaign ? (
                            <span
                              title="Linked to Campaign + Reel Linked"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200"
                            >
                              <CheckCircle2 className="w-3 h-3 text-blue-600" />
                              Linked to Campaign + Reel Linked
                            </span>
                          ) : (
                            <span
                              title="Added Reel"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200"
                            >
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              Added Reel
                            </span>
                          )
                        ) : isLimitError ? (
                          <span
                            title={item.error}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200"
                          >
                            <AlertCircle className="w-3 h-3 text-amber-600 shrink-0" />
                            Limit Reached
                          </span>
                        ) : (
                          <span
                            title={item.error}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 max-w-[220px] truncate"
                          >
                            <AlertCircle className="w-3 h-3 text-rose-600 shrink-0" />
                            {item.error || 'Failed'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <DialogFooter className="pt-3 border-t border-slate-100 flex items-center justify-between sm:justify-between w-full">
              {isProcessing ? (
                <div className="flex items-center justify-between w-full">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancel}
                    className="border-slate-200 text-slate-600 hover:text-slate-900 rounded-xl text-xs h-8 px-3"
                  >
                    Cancel Import
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between w-full">
                  {failedCount > 0 ? (
                    <Button
                      variant="outline"
                      onClick={handleRetryFailed}
                      className="border-slate-200 text-slate-700 rounded-xl flex items-center gap-1.5 text-xs"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Retry Failed ({failedCount})
                    </Button>
                  ) : (
                    <div />
                  )}
                  <Button
                    onClick={() => handleOpenChange(false)}
                    className="bg-[#A655F8] hover:bg-[#9042E0] text-white rounded-xl px-5 py-2 font-semibold shadow-sm"
                  >
                    Done
                  </Button>
                </div>
              )}
            </DialogFooter>
          </>
        ) : mode === 'choose' ? (
          /* Method Chooser View (matches attached design) */
          <div>
            <div className="flex items-center gap-4 text-left">
              <div className="relative w-14 h-14 rounded-full bg-[#FAF5FF] flex items-center justify-center shrink-0">
                <Link2 className="w-7 h-7 text-[#A655F8] stroke-[1.8]" />
                <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#A655F8] flex items-center justify-center text-white border-2 border-white shadow-sm">
                  <Plus className="w-3 h-3 stroke-[3]" />
                </div>
              </div>
              <div>
                <DialogTitle className="text-[20px] sm:text-[22px] font-bold text-slate-900 leading-tight">
                  Upload Links
                </DialogTitle>
                <p className="text-[13px] sm:text-[14px] text-slate-500 font-normal mt-1 leading-snug">
                  Choose how you'd like to add links to this campaign.
                </p>
              </div>
            </div>

            <div className="mt-6 space-y-3.5 relative z-10">
              {/* Option 1: Add URL */}
              <button
                type="button"
                onClick={() => setMode('url')}
                className="w-full flex items-center gap-3.5 sm:gap-4 rounded-[16px] border border-slate-200 p-4 text-left bg-white hover:border-[#A655F8]/70 hover:bg-[#FAF5FF]/30 transition-all duration-150 group"
              >
                <div className="w-12 h-12 rounded-[14px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0">
                  <Link2 className="w-5 h-5 stroke-[2]" />
                </div>
                <div className="flex-grow min-w-0">
                  <h5 className="text-[15px] font-bold text-slate-900">
                    Add URL
                  </h5>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    Paste Instagram reel or post links line by line
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
              </button>

              {/* Option 2: Upload CSV */}
              <button
                type="button"
                onClick={() => setMode('csv')}
                className="w-full flex items-center gap-3.5 sm:gap-4 rounded-[16px] border border-slate-200 p-4 text-left bg-white hover:border-[#A655F8]/70 hover:bg-[#FAF5FF]/30 transition-all duration-150 group"
              >
                <div className="w-12 h-12 rounded-[14px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0">
                  <Upload className="w-5 h-5 stroke-[2]" />
                </div>
                <div className="flex-grow min-w-0">
                  <h5 className="text-[15px] font-bold text-slate-900">
                    Upload CSV
                  </h5>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    Upload a CSV file containing only links
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
              </button>

              {/* Option 3: Add Post */}
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onSelectAddPost?.();
                }}
                className="w-full flex items-center gap-3.5 sm:gap-4 rounded-[16px] border border-slate-200 p-4 text-left bg-white hover:border-[#A655F8]/70 hover:bg-[#FAF5FF]/30 transition-all duration-150 group"
              >
                <div className="w-12 h-12 rounded-[14px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0">
                  <Plus className="w-5 h-5 stroke-[2]" />
                </div>
                <div className="flex-grow min-w-0">
                  <h5 className="text-[15px] font-bold text-slate-900">
                    Add Post
                  </h5>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    Select influencer, platform, and content type
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>

            {/* Footer security note */}
            <div className="mt-6 pt-1 flex items-center gap-2.5 text-slate-500 text-[12px] font-normal leading-tight relative z-10">
              <ShieldCheck className="w-4 h-4 text-[#A655F8] shrink-0 stroke-[1.8]" />
              <span>
                Your data is secure and will only be used
                <br />
                for this campaign.
              </span>
            </div>

            {/* Decorative soft purple curve and sparkles in bottom right */}
            <div className="absolute -bottom-1 -right-1 w-44 h-32 pointer-events-none select-none overflow-hidden">
              <svg
                viewBox="0 0 180 140"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full text-[#F3E8FF]"
              >
                <path
                  d="M180 20C130 35 95 75 55 105C30 125 10 135 0 140H180V20Z"
                  fill="currentColor"
                />
              </svg>
              <span className="absolute bottom-5 right-7 text-[#A855F7] text-[13px] leading-none">
                ✦
              </span>
              <span className="absolute bottom-10 right-3.5 text-[#C084FC] text-[10px] leading-none">
                ✦
              </span>
            </div>
          </div>
        ) : mode === 'url' ? (
          /* Add URL View */
          <>
            <DialogHeader className="pb-3 border-b border-slate-100 flex flex-row items-center gap-3 text-left">
              <button
                type="button"
                onClick={() => setMode('choose')}
                className="w-9 h-9 rounded-full bg-[#FAF5FF] hover:bg-[#F3E8FF] text-[#A655F8] flex items-center justify-center shrink-0 transition-colors"
                title="Back to options"
              >
                <ArrowLeft className="w-4 h-4 stroke-[2.5]" />
              </button>
              <div className="w-10 h-10 rounded-full bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#E9D5FF]">
                <Link2 className="w-5 h-5 stroke-[2]" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  Add URL
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Paste Instagram reel or post links line by line.
                </p>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto py-4 space-y-3.5">
              <div className="flex items-center justify-between">
                <label className="text-[14px] font-semibold text-slate-800">
                  One link in a row
                </label>
                <div className="flex items-center gap-2">
                  {duplicateCount > 0 && (
                    <span className="text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                      {duplicateCount} duplicate{duplicateCount === 1 ? '' : 's'} link
                    </span>
                  )}
                  {normalizedUrls.length > 0 && (
                    <span className="text-[12px] font-medium text-[#A655F8] bg-[#FAF5FF] px-2.5 py-0.5 rounded-full border border-[#E9D5FF]">
                      {normalizedUrls.length} unique link{normalizedUrls.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </div>

              <Textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={`https://www.instagram.com/reel/DEz99abc123/\nhttps://www.instagram.com/reel/C8XYZ12345/\nhttps://www.instagram.com/p/C9ABC67890/`}
                className="w-full min-h-[160px] text-sm text-slate-800 rounded-xl border border-slate-200 p-3.5 focus:border-[#A655F8] focus-visible:border-[#A655F8] !ring-0 !ring-offset-0 focus:!ring-0 focus:!ring-offset-0 focus-visible:!ring-0 focus-visible:!ring-offset-0 !outline-none focus:!outline-none focus-visible:!outline-none placeholder-slate-400 font-mono resize-none [&::-webkit-resizer]:hidden shadow-none transition-colors"
              />

              {isCapped && (
                <div className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200">
                  ⚠️ Capped at {MAX_URLS_PER_IMPORT} links per batch for optimal performance ({rawCount} entered).
                </div>
              )}

              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-[#FAF5FF] border border-[#E9D5FF]/60 text-[#7E22CE] text-[12px] leading-relaxed">
                <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-[#A655F8]" />
                <span>
                  Creators missing from your database will be automatically enriched (followers, engagement rate, average likes, and views) and assigned to this campaign.
                </span>
              </div>
            </div>

            <DialogFooter className="pt-3 border-t border-slate-100 flex items-center justify-between sm:justify-between w-full">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setMode('choose')}
                className="text-slate-500 hover:text-slate-700 rounded-xl"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={handleStartUrlImport}
                disabled={normalizedUrls.length === 0}
                className="bg-[#A655F8] hover:bg-[#9042E0] text-white rounded-xl px-5 py-2 font-semibold shadow-sm flex items-center gap-1.5 transition-all"
              >
                <span>Submit</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </DialogFooter>
          </>
        ) : (
          /* Upload CSV View */
          <>
            <DialogHeader className="pb-3 border-b border-slate-100 flex flex-row items-center gap-3 text-left">
              <button
                type="button"
                onClick={() => setMode('choose')}
                className="w-9 h-9 rounded-full bg-[#FAF5FF] hover:bg-[#F3E8FF] text-[#A655F8] flex items-center justify-center shrink-0 transition-colors"
                title="Back to options"
              >
                <ArrowLeft className="w-4 h-4 stroke-[2.5]" />
              </button>
              <div className="w-10 h-10 rounded-full bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#E9D5FF]">
                <Upload className="w-5 h-5 stroke-[2]" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  Upload CSV
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Upload a CSV file containing only links to import.
                </p>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto py-4 space-y-3.5">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                className="hidden"
              />

              {!csvFile ? (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer flex flex-col items-center justify-center gap-3 ${isDragOver
                    ? 'border-[#A655F8] bg-[#FAF5FF]'
                    : 'border-slate-200 hover:border-[#A655F8]/60 bg-slate-50/50 hover:bg-[#FAF5FF]/40'
                    }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#FAF5FF] border border-[#E9D5FF] flex items-center justify-center text-[#A655F8] shadow-xs">
                    <Upload className="w-6 h-6 stroke-[2]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      Click to upload or drag & drop
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      CSV or TXT file containing only links (one per line)
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="border border-slate-200 rounded-xl p-3.5 bg-white flex items-center justify-between gap-3 shadow-xs">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-[#FAF5FF] border border-[#E9D5FF] flex items-center justify-center text-[#A655F8] shrink-0">
                        <FileSpreadsheet className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {csvFile.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {(csvFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {csvExtractedUrls.length > 0 ? (
                        <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                          {csvExtractedUrls.length} link{csvExtractedUrls.length === 1 ? '' : 's'} found
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-rose-700 bg-rose-50 px-2.5 py-1 rounded-full border border-rose-200">
                          No links found
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCsvFile(null);
                          setCsvExtractedUrls([]);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Preview of links found */}
                  {csvExtractedUrls.length > 0 && (
                    <div className="border border-slate-100 rounded-xl p-3 bg-slate-50/70 space-y-1.5 max-h-[120px] overflow-y-auto">
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                        Preview of links
                      </div>
                      {csvExtractedUrls.slice(0, 3).map((url, i) => (
                        <div key={i} className="text-xs font-mono text-slate-600 truncate">
                          {url}
                        </div>
                      ))}
                      {csvExtractedUrls.length > 3 && (
                        <div className="text-[11px] font-medium text-[#A655F8]">
                          + {csvExtractedUrls.length - 3} more link{csvExtractedUrls.length - 3 === 1 ? '' : 's'}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs font-semibold text-[#A655F8] hover:underline"
                    >
                      Choose a different file
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-[#FAF5FF] border border-[#E9D5FF]/60 text-[#7E22CE] text-[12px] leading-relaxed">
                <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-[#A655F8]" />
                <span>
                  Creators missing from your database will be automatically enriched (followers, engagement rate, average likes, and views) and assigned to this campaign.
                </span>
              </div>
            </div>

            <DialogFooter className="pt-3 border-t border-slate-100 flex items-center justify-between sm:justify-between w-full">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setMode('choose')}
                className="text-slate-500 hover:text-slate-700 rounded-xl"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={handleStartCsvImport}
                disabled={csvExtractedUrls.length === 0}
                className="bg-[#A655F8] hover:bg-[#9042E0] text-white rounded-xl px-5 py-2 font-semibold shadow-sm flex items-center gap-1.5 transition-all"
              >
                <span>Submit</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
